(() => {
  class SonTienTabs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.tabController = new AbortController();
      const signal = this.tabController.signal;
      const tabs = [...this.querySelectorAll('[role="tab"]')];
      const panels = [...this.querySelectorAll('[role="tabpanel"]')];
      if (!tabs.length || !panels.length) return;
      const previousCard = this.querySelector('[data-booking-previous]');
      const nextCard = this.querySelector('[data-booking-next]');
      const updateCards = () => {
        const panel = panels.find(item => !item.hidden);
        if (!panel) return;
        if (previousCard) previousCard.disabled = panel.scrollLeft < 2;
        if (nextCard) nextCard.disabled = panel.scrollLeft >= panel.scrollWidth - panel.clientWidth - 2;
      };
      const select = (tab, focus = false) => {
        tabs.forEach(item => {
          const active = item === tab;
          item.setAttribute('aria-selected', String(active));
          item.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.id !== tab.getAttribute('aria-controls'); });
        if (focus) tab.focus();
        requestAnimationFrame(updateCards);
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab), { signal });
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = tabs.length - 1;
          if (next !== undefined) { event.preventDefault(); select(tabs[next], true); }
        }, { signal });
      });
      const moveCards = direction => {
        const panel = panels.find(item => !item.hidden);
        if (panel) panel.scrollBy({left:direction * (panel.children[0].getBoundingClientRect().width + 16),behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      };
      previousCard?.addEventListener('click',()=>moveCards(-1),{signal});
      nextCard?.addEventListener('click',()=>moveCards(1),{signal});
      panels.forEach(panel=>panel.addEventListener('scroll',updateCards,{passive:true,signal}));
      this.cardObserver = new ResizeObserver(updateCards);
      panels.forEach(panel=>this.cardObserver.observe(panel));
      select(tabs[0]);
      this.addEventListener('shopify:block:select', event => {
        const panel = event.target.closest('[role="tabpanel"]');
        const tab = tabs.find(item => item.getAttribute('aria-controls') === panel?.id);
        if (tab) select(tab);
      }, { signal });
      this.classList.add('st26-tabs-ready');
    }
    disconnectedCallback(){this.tabController?.abort();this.cardObserver?.disconnect();this.ready=false;}
  }
  if (!customElements.get('son-tien-tabs')) customElements.define('son-tien-tabs', SonTienTabs);
  class SonTienCarousel extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const track = this.querySelector('[data-track]');
      if (!track) return;
      const originals = [...track.children];
      const count = originals.length;
      if (!count) return;
      const prev = this.querySelector('[data-previous]');
      const next = this.querySelector('[data-next]');
      const dots = [...this.querySelectorAll('[data-slide]')];
      const status = this.querySelector('[data-status]');
      let current = 0, resizing = false;
      const clone = item => {
        const copy = item.cloneNode(true);
        copy.dataset.clone = '';
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        copy.removeAttribute('data-shopify-editor-block');
        copy.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
        return copy;
      };
      if (count > 1) {
        track.prepend(...originals.map(clone));
        track.append(...originals.map(clone));
      }
      this.classList.add('st26-carousel-ready');
      const all = [...track.children];
      const base = count > 1 ? count : 0;
      const centerAt = (index, smooth = false) => {
        const item = all[index];
        const rect = item.getBoundingClientRect();
        const frame = track.getBoundingClientRect();
        track.scrollTo({left: track.scrollLeft + rect.left - frame.left + rect.width / 2 - track.clientWidth / 2,
          behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant'});
      };
      const nearest = () => {
        const center = track.getBoundingClientRect().left + track.clientWidth / 2;
        return all.reduce((best,item,i) => {
          const r = item.getBoundingClientRect(), b = all[best].getBoundingClientRect();
          return Math.abs(r.left+r.width/2-center) < Math.abs(b.left+b.width/2-center) ? i : best;
        },0);
      };
      const announce = () => {
        if (status) status.textContent = `${current + 1} / ${count}`;
        dots.forEach((dot,i) => i === current ? dot.setAttribute('aria-current','true') : dot.removeAttribute('aria-current'));
      };
      const settle = () => {
        if (resizing) return;
        const index = nearest();
        current = ((index-base)%count+count)%count;
        announce();
        if (count > 1 && (index < base || index >= base+count)) centerAt(base+current);
      };
      const move = direction => {
        const index = nearest();
        centerAt(Math.max(0,Math.min(all.length-1,index+direction)),true);
      };
      prev?.addEventListener('click',()=>move(-1),{signal});
      next?.addEventListener('click',()=>move(1),{signal});
      if (prev) prev.disabled = count < 2;
      if (next) next.disabled = count < 2;
      dots.forEach((dot,i)=>dot.addEventListener('click',()=>centerAt(base+i,true),{signal}));
      track.addEventListener('keydown',event=>{
        if (event.target !== track) return;
        if (event.key==='ArrowRight'||event.key==='ArrowLeft') {event.preventDefault();move(event.key==='ArrowRight'?1:-1);}
        if (event.key==='Home'||event.key==='End') {event.preventDefault();centerAt(base+(event.key==='Home'?0:count-1),true);}
      },{signal});
      track.addEventListener('scroll',()=>{clearTimeout(this.timer);this.timer=setTimeout(settle,160);},{passive:true,signal});
      track.addEventListener('scrollend',settle,{signal});
      this.addEventListener('shopify:block:select',event=>{const i=originals.indexOf(event.target.closest('article'));if(i>=0)centerAt(base+i);},{signal});
      let lastWidth = 0;
      this.observer = new ResizeObserver(()=>{
        if (track.clientWidth === lastWidth) return;
        lastWidth = track.clientWidth; resizing = true;
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(()=>{centerAt(base+current);resizing=false;});
      });
      this.observer.observe(track);
      centerAt(base);announce();
    }
    disconnectedCallback() {
      this.controller?.abort();this.observer?.disconnect();clearTimeout(this.timer);cancelAnimationFrame(this.frame);
      this.querySelectorAll('[data-clone]').forEach(item=>item.remove());this.ready=false;
    }
  }
  if (!customElements.get('son-tien-carousel')) customElements.define('son-tien-carousel', SonTienCarousel);
  class SonTienMarquee extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const button = this.querySelector('button');
      button?.addEventListener('click',()=>{
        const paused=this.classList.toggle('is-paused');
        button.setAttribute('aria-pressed',String(paused));
        button.setAttribute('aria-label',paused?'Tiếp tục chuyển động ảnh và logo':'Tạm dừng chuyển động ảnh và logo');
        button.textContent=paused?'▶':'Ⅱ';
      },{signal:this.controller.signal});
    }
    disconnectedCallback(){this.controller?.abort();this.controller=null;}
  }
  if (!customElements.get('son-tien-marquee')) customElements.define('son-tien-marquee',SonTienMarquee);
})();
